import * as ents from '@attain-apps/entities'

import type { Entity } from '../../types'

/**
 * State for the sender plugin
 */
export interface State {
  /**
   * The current conversation's info entries, by the entry's id.
   *
   * Emptied, like the collection's cache, whenever the conversation changes.
   */
  readonly infoEntriesById: Readonly<
    Record<ents.core.agent.Info.Stored.Id, ents.core.agent.Info.Stored>
  >
  /**
   * Messages sent from here that no read of their conversation has listed yet,
   * by id.
   *
   * Kept apart from the collection's cache, which is emptied whenever the
   * conversation changes, as it does when a message starts a new one.
   */
  readonly sentById: Readonly<Record<Entity['id'], Entity>>
}

export namespace State {
  /**
   * Initial state for the plugin
   */
  export const INITIAL: State = { infoEntriesById: {}, sentById: {} }

  /** The state with the given message remembered as sent. */
  export function withSent(state: State, message: Entity): State {
    return { ...state, sentById: { ...state.sentById, [message.id]: message } }
  }

  /** The state with the given sent messages forgotten. */
  export function withoutSent(
    state: State,
    ids: readonly Entity['id'][],
  ): State {
    if (!ids.some(id => id in state.sentById)) {
      return state
    }

    const sentById = { ...state.sentById }
    for (const id of ids) {
      delete sentById[id]
    }

    return { ...state, sentById }
  }

  /** The state with the given entries taken in. */
  export function withInfoEntries(
    state: State,
    entries: readonly ents.core.agent.Info.Stored[],
  ): State {
    if (entries.length === 0) {
      return state
    }

    const infoEntriesById = { ...state.infoEntriesById }
    for (const entry of entries) {
      infoEntriesById[ents.core.agent.Info.Stored.idOf(entry)] = entry
    }

    return { ...state, infoEntriesById }
  }

  /** The state with the given message's entries forgotten. */
  export function withoutInfoEntries(
    state: State,
    messageId: ents.core.agent.Message['id'],
  ): State {
    const kept = Object.entries(state.infoEntriesById).filter(
      ([, stored]) => stored.messageId !== messageId,
    )
    if (kept.length === Object.keys(state.infoEntriesById).length) {
      return state
    }

    return {
      ...state,
      infoEntriesById: Object.fromEntries(kept) as State['infoEntriesById'],
    }
  }

  /** The state with every info entry forgotten. */
  export function withoutAnyInfoEntries(state: State): State {
    return Object.keys(state.infoEntriesById).length === 0
      ? state
      : { ...state, infoEntriesById: State.INITIAL.infoEntriesById }
  }
}
